// REST + WebSocket client. Tokens live ONLY in sessionStorage and never in
// console.log. The WS handshake passes tokens as query params; once the
// server has validated and attached the role, subsequent messages do not
// re-send the token.

import type { ScenarioCatalogEntry, ServerMessage, SessionBrief } from "./types";

export interface SessionTokens {
  sessionToken: string;
  imToken?: string;
  teamToken?: string;
  playerId?: string;
}

const STORAGE_KEY = "breakpoint.session.v1";

export function loadStoredSession(): SessionTokens | undefined {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as SessionTokens;
  } catch {
    return undefined;
  }
}

export function saveStoredSession(t: SessionTokens): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(t));
}

export function clearStoredSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export async function fetchScenarios(): Promise<ScenarioCatalogEntry[]> {
  const res = await fetch("/api/scenarios");
  if (!res.ok) throw new Error("scenarios-failed");
  const json = (await res.json()) as { scenarios: ScenarioCatalogEntry[] };
  return json.scenarios;
}

export async function fetchSessionBrief(sessionToken: string): Promise<SessionBrief> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionToken)}/brief`);
  if (!res.ok) throw new Error("brief-failed");
  return (await res.json()) as SessionBrief;
}

export async function createSession(scenarioId?: string): Promise<{ sessionToken: string; imToken: string }> {
  const res = await fetch("/api/sessions", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ scenarioId }),
  });
  if (!res.ok) throw new Error("create-failed");
  return await res.json();
}

export async function createTeam(sessionToken: string, teamName: string): Promise<{ teamToken: string; teamId: string }> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionToken)}/teams`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ teamName }),
  });
  if (!res.ok) throw new Error("team-failed");
  return await res.json();
}

export async function setScenario(
  sessionToken: string,
  imToken: string,
  scenarioId: string,
): Promise<{ scenarioId: string; scenarioName: string }> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionToken)}/scenario`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ imToken, scenarioId }),
  });
  if (!res.ok) throw new Error("scenario-set-failed");
  return await res.json();
}

export async function startGame(sessionToken: string, imToken: string): Promise<void> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionToken)}/start`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ imToken }),
  });
  if (!res.ok) {
    // Surface the server's machine-readable code (e.g. "start:no-teams") so the
    // UI can explain the failure instead of silently doing nothing.
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || "start-failed");
  }
}

export async function rematchSession(
  sessionToken: string,
  imToken: string,
  scenarioId?: string,
): Promise<{
  newSessionToken: string;
  newImToken: string;
  scenarioId: string;
  scenarioName: string;
  teams: { oldTeamId: string; newTeamId: string; newTeamToken: string }[];
}> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionToken)}/rematch`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ imToken, scenarioId }),
  });
  if (!res.ok) throw new Error("rematch-failed");
  return await res.json();
}

// ---------- WebSocket ----------

export interface ConnectOpts {
  sessionToken: string;
  teamToken?: string;
  imToken?: string;
  playerId?: string;
  handle?: string;
  teamRole?: "soc" | "ir-lead" | "comms";
  onMessage: (m: ServerMessage) => void;
  onClose?: (info: { receivedAny: boolean; clean: boolean }) => void;
  onOpen?: () => void;
}

export class GameSocket {
  private ws?: WebSocket;
  private nonce = 0;
  private opts: ConnectOpts;
  private receivedAny = false;
  private explicitlyClosed = false;
  constructor(opts: ConnectOpts) {
    this.opts = opts;
  }
  connect(): void {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const params = new URLSearchParams({ session: this.opts.sessionToken });
    if (this.opts.teamToken) params.set("team", this.opts.teamToken);
    if (this.opts.imToken) params.set("im", this.opts.imToken);
    if (this.opts.playerId) params.set("player", this.opts.playerId);
    if (this.opts.handle) params.set("handle", this.opts.handle);
    if (this.opts.teamRole) params.set("teamRole", this.opts.teamRole);
    const url = `${proto}//${location.host}/ws/${encodeURIComponent(this.opts.sessionToken)}?${params}`;
    const ws = new WebSocket(url);
    ws.onopen = () => {
      this.opts.onOpen?.();
      this.send({ type: "hello", reconnectPlayerId: this.opts.playerId });
    };
    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data) as ServerMessage;
        this.receivedAny = true;
        this.opts.onMessage(m);
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => this.opts.onClose?.({ receivedAny: this.receivedAny, clean: this.explicitlyClosed });
    this.ws = ws;
  }
  send(msg: { type: string; [k: string]: unknown }): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const payload = JSON.stringify({ ...msg, nonce: ++this.nonce });
    this.ws.send(payload);
  }
  close(): void {
    this.explicitlyClosed = true;
    this.ws?.close();
  }
}
